/**
 * Error Fingerprinting — ported from PSA v1
 *
 * Creates unique fingerprints for errors to enable intelligent grouping.
 * Zero external imports.
 */

import type { ErrorCategory, ErrorSeverity, BrowserInfo } from './types';

// ─── Normalization Patterns ──────────────────────────────────

const NORMALIZE_PATTERNS: { pattern: RegExp; replacement: string }[] = [
  { pattern: /[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}/gi, replacement: '<UUID>' },
  { pattern: /[0-9a-f]{24}/gi, replacement: '<ID>' },
  { pattern: /[\w.-]+@[\w.-]+\.\w+/gi, replacement: '<EMAIL>' },
  { pattern: /\b\d{5,}\b/g, replacement: '<NUMBER>' },
  { pattern: /\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}[.\d]*Z?/g, replacement: '<TIMESTAMP>' },
  { pattern: /\b1[6-7]\d{11}\b/g, replacement: '<UNIX_TS>' },
  { pattern: /\b\d{1,3}\.\d{1,3}\.\d{1,3}\.\d{1,3}\b/g, replacement: '<IP>' },
  { pattern: /\/[a-z]+\/[0-9a-f-]+\//gi, replacement: '/<entity>/<ID>/' },
  { pattern: /eyJ[a-zA-Z0-9_-]+\.eyJ[a-zA-Z0-9_-]+\.[a-zA-Z0-9_-]+/g, replacement: '<JWT>' },
  { pattern: /=[0-9a-f-]{20,}/gi, replacement: '=<VALUE>' },
];

export function normalizeErrorMessage(message: string): string {
  if (!message) return '';
  let normalized = message;
  for (const { pattern, replacement } of NORMALIZE_PATTERNS) {
    normalized = normalized.replace(pattern, replacement);
  }
  return normalized.replace(/\s+/g, ' ').trim();
}

// ─── Fingerprint Generation ─────────────────────────────────

function simpleHash(str: string): string {
  let hash = 5381;
  for (let i = 0; i < str.length; i++) {
    hash = ((hash << 5) + hash) ^ str.charCodeAt(i);
  }
  return (hash >>> 0).toString(16).padStart(8, '0');
}

export function generateErrorFingerprint(params: {
  errorMessage: string;
  actionAttempted: string;
  requestUrl?: string;
  errorCode?: string;
}): string {
  const normalized = normalizeErrorMessage(params.errorMessage);
  const action = params.actionAttempted.toLowerCase().trim();
  let urlPattern = '';
  if (params.requestUrl) {
    try {
      const url = new URL(params.requestUrl, 'http://localhost');
      urlPattern = normalizeErrorMessage(url.pathname);
    } catch {
      urlPattern = normalizeErrorMessage(params.requestUrl.split('?')[0] || '');
    }
  }
  const components = [action, normalized, urlPattern, params.errorCode || ''].filter(Boolean);
  return `fp1-${simpleHash(components.join('|'))}`;
}

// ─── Category Detection ─────────────────────────────────────

const CATEGORY_PATTERNS: { pattern: RegExp; category: ErrorCategory }[] = [
  { pattern: /unauthorized|401|not authenticated|session expired/i, category: 'auth_error' },
  { pattern: /forbidden|403|access denied|permission denied/i, category: 'permission_error' },
  { pattern: /network|fetch|CORS|connection refused|ECONNREFUSED|ERR_NETWORK/i, category: 'network_error' },
  { pattern: /timeout|timed out|ESOCKETTIMEDOUT/i, category: 'timeout_error' },
  { pattern: /500|internal server error/i, category: 'api_error' },
  { pattern: /404|not found/i, category: 'not_found' },
  { pattern: /400|bad request|invalid request/i, category: 'validation_error' },
  { pattern: /429|rate limit|too many requests/i, category: 'rate_limit' },
  { pattern: /cosmos|database|query|partition/i, category: 'database_error' },
  { pattern: /validation|invalid|required field/i, category: 'validation_error' },
  { pattern: /third.?party|integration/i, category: 'integration_error' },
  { pattern: /render|component|react|undefined is not|cannot read property/i, category: 'ui_error' },
];

export function detectErrorCategory(message: string, statusCode?: number): ErrorCategory {
  if (statusCode) {
    if (statusCode === 401) return 'auth_error';
    if (statusCode === 403) return 'permission_error';
    if (statusCode === 404) return 'not_found';
    if (statusCode === 429) return 'rate_limit';
    if (statusCode >= 500) return 'api_error';
    if (statusCode === 400) return 'validation_error';
  }
  const lower = message.toLowerCase();
  for (const { pattern, category } of CATEGORY_PATTERNS) {
    if (pattern.test(lower)) return category;
  }
  return 'unknown';
}

// ─── Severity Detection ─────────────────────────────────────

export function detectErrorSeverity(message: string, category: ErrorCategory): ErrorSeverity {
  if (category === 'database_error') return 'critical';
  if (category === 'api_error' && /500/i.test(message)) return 'critical';
  if (category === 'auth_error' || category === 'permission_error') return 'high';
  if (/fatal|crash|data loss|corruption|critical/i.test(message)) return 'critical';
  if (/failed to load|failed to fetch|network error/i.test(message)) return 'high';
  if (/timeout|slow|retry/i.test(message)) return 'medium';
  if (/warning|deprecated/i.test(message)) return 'low';
  const defaults: Record<string, ErrorSeverity> = {
    api_error: 'high', network_error: 'high', database_error: 'critical',
    auth_error: 'high', permission_error: 'high', integration_error: 'high',
    validation_error: 'medium', timeout_error: 'medium', rate_limit: 'medium',
    not_found: 'medium', ui_error: 'medium', unknown: 'medium',
  };
  return defaults[category] || 'medium';
}

// ─── Browser Info ────────────────────────────────────────────

export function getBrowserInfo(): BrowserInfo {
  if (typeof window === 'undefined') return {};
  return {
    userAgent: navigator.userAgent,
    language: navigator.language,
    platform: navigator.platform,
    screenWidth: window.screen?.width,
    screenHeight: window.screen?.height,
    url: window.location?.href,
    referrer: document.referrer || undefined,
  };
}

// ─── Sanitization ────────────────────────────────────────────

const SENSITIVE_FIELDS = ['password', 'token', 'apikey', 'api_key', 'secret', 'credential', 'authorization'];

export function sanitizePayload(obj: unknown): unknown {
  if (obj === null || obj === undefined) return obj;
  if (typeof obj !== 'object') return obj;
  if (Array.isArray(obj)) return obj.map(sanitizePayload);
  const sanitized: Record<string, unknown> = {};
  for (const [key, value] of Object.entries(obj as Record<string, unknown>)) {
    const lower = key.toLowerCase();
    if (SENSITIVE_FIELDS.some(f => lower.includes(f))) {
      sanitized[key] = '[REDACTED]';
    } else if (typeof value === 'object' && value !== null) {
      sanitized[key] = sanitizePayload(value);
    } else {
      sanitized[key] = value;
    }
  }
  return sanitized;
}

// ─── Error Parser ────────────────────────────────────────────

export function parseError(error: unknown): { message: string; code?: string; stack?: string } {
  if (error == null) return { message: 'Unknown error (null)' };
  if (error instanceof Error) {
    return { message: error.message, stack: error.stack, code: (error as Error & { code?: string }).code };
  }
  if (typeof error === 'object') {
    const obj = error as Record<string, unknown>;
    const message = (obj.message as string) || (obj.error as string) || (obj.errorMessage as string) || JSON.stringify(obj);
    return { message, code: (obj.code as string) || (obj.statusCode as string)?.toString() };
  }
  if (typeof error === 'string') return { message: error };
  return { message: String(error) };
}
